import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  NativeSelectField,
  NativeSelectRoot,
  SimpleGrid,
  Stack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { FaPlus } from "react-icons/fa"
import {
  FiBarChart2,
  FiCopy,
  FiDownload,
  FiEdit2,
  FiMoreVertical,
  FiSearch,
  FiTrash2,
} from "react-icons/fi"

import {
  type ApiError,
  ProgramsService,
  type QuizListPublic_Output as QuizListPublic,
  QuizTemplatesService,
  QuizzesService,
} from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { QuizFilterControls } from "@/components/Quizzes/QuizFilterControls"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
} from "@/components/ui/menu"
import useCustomToast from "@/hooks/useCustomToast"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { handleError } from "@/utils"
import { formatDateTime } from "@/utils/format-date"
import { getQuizPublishStatus } from "@/utils/quiz-status"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type QuizRow = QuizListPublic

type ProgramFilter =
  | { kind: "all" }
  | { kind: "unassigned" }
  | { kind: "program"; id: string }

type TagFilter = string[]

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const FILTER_STORAGE_KEY = "quiz-management:program-filter"
const TAG_FILTER_STORAGE_KEY = "quiz-management:tag-filter"
const MAX_BULK_QUIZZES = 200

function loadFilterFromStorage(): ProgramFilter {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY)
    if (!raw) return { kind: "all" }
    const parsed = JSON.parse(raw) as ProgramFilter
    if (
      parsed.kind === "all" ||
      parsed.kind === "unassigned" ||
      (parsed.kind === "program" &&
        typeof (parsed as { kind: "program"; id: string }).id === "string")
    ) {
      return parsed
    }
  } catch {
    // malformed — fall through to default
  }
  return { kind: "all" }
}

function saveFilterToStorage(filter: ProgramFilter): void {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter))
}

function loadTagFilterFromStorage(): TagFilter {
  const raw = localStorage.getItem(TAG_FILTER_STORAGE_KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((tag): tag is string => typeof tag === "string")
      : typeof parsed === "string"
        ? [parsed]
        : []
  } catch {
    return [raw]
  }
}

function saveTagFilterToStorage(tag: TagFilter): void {
  if (tag.length > 0) {
    localStorage.setItem(TAG_FILTER_STORAGE_KEY, JSON.stringify(tag))
  } else {
    localStorage.removeItem(TAG_FILTER_STORAGE_KEY)
  }
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_layout/quiz-management/")({
  component: QuizManagementList,
})

function QuizManagementList() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { showSuccessToast, showErrorToast } = useCustomToast()
  const [nameFilter, setNameFilter] = useState("")
  const [deleteTarget, setDeleteTarget] = useState<QuizListPublic | null>(null)
  const [programFilter, setProgramFilterState] = useState<ProgramFilter>(
    loadFilterFromStorage,
  )
  const [tagFilter, setTagFilterState] = useState<TagFilter>(
    loadTagFilterFromStorage,
  )
  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedQuizIds, setSelectedQuizIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [isBulkApplyOpen, setIsBulkApplyOpen] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState("")

  function setProgramFilter(next: ProgramFilter) {
    setProgramFilterState(next)
    saveFilterToStorage(next)
  }

  function setTagFilter(next: TagFilter) {
    setTagFilterState(next)
    saveTagFilterToStorage(next)
  }

  const { data, isLoading } = useQuery({
    queryKey: ["quizzes", programFilter, tagFilter, selectedSubjects],
    queryFn: () => {
      const baseParams = {
        limit: 100,
        ...(tagFilter.length > 0 ? { tags: tagFilter } : {}),
        ...(selectedSubjects.length > 0
          ? { subjectIds: selectedSubjects.map((subject) => subject.id) }
          : {}),
      }
      if (programFilter.kind === "unassigned") {
        return QuizzesService.listQuizzes({
          ...baseParams,
          unassignedOnly: true,
        })
      }
      if (programFilter.kind === "program") {
        return QuizzesService.listQuizzes({
          ...baseParams,
          programId: programFilter.id,
        })
      }
      return QuizzesService.listQuizzes(baseParams)
    },
  })

  const { data: subjectOptionsData } = useQuery({
    queryKey: ["quizzes", "subject-options"],
    queryFn: () => QuizzesService.listQuizzes({ limit: 200 }),
  })

  const availableSubjects = useMemo(() => {
    const subjects = new Map<string, { id: string; name: string }>()
    for (const quiz of subjectOptionsData?.data ?? []) {
      for (const subject of quiz.subjects ?? [])
        subjects.set(subject.id, subject)
    }
    return [...subjects.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [subjectOptionsData?.data])

  const { data: programsData } = useQuery({
    queryKey: ["programs"],
    queryFn: () => ProgramsService.listPrograms({ limit: 200 }),
  })

  const programs = programsData?.data ?? []

  const deleteQuizMutation = useMutation({
    mutationFn: (quizId: string) => QuizzesService.deleteQuiz({ quizId }),
    onSuccess: () => {
      showSuccessToast("Quiz deleted successfully")
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: ["quizzes"] })
      queryClient.invalidateQueries({ queryKey: ["quiz-tags"] })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const duplicateQuizMutation = useMutation({
    mutationFn: (quizId: string) => QuizzesService.duplicateQuiz({ quizId }),
    onSuccess: (duplicatedQuiz) => {
      showSuccessToast("Quiz duplicated successfully")
      queryClient.invalidateQueries({ queryKey: ["quizzes"] })
      queryClient.invalidateQueries({ queryKey: ["quiz-tags"] })
      navigate({ to: `/quiz-management/${duplicatedQuiz.id}/edit` })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const { data: templatesData } = useQuery({
    queryKey: ["quiz-templates"],
    queryFn: () => QuizTemplatesService.listQuizTemplates(),
    enabled: isBulkApplyOpen,
  })

  const bulkApplyTemplateMutation = useMutation({
    mutationFn: () =>
      QuizTemplatesService.applyQuizTemplate({
        templateId: selectedTemplateId,
        requestBody: { quiz_ids: Array.from(selectedQuizIds) },
      }),
    onSuccess: ({ updated_count: updatedCount }) => {
      showSuccessToast(
        `Template applied to ${updatedCount} ${
          updatedCount === 1 ? "quiz" : "quizzes"
        }`,
      )
      setSelectedQuizIds(new Set())
      setSelectedTemplateId("")
      setIsBulkApplyOpen(false)
      queryClient.invalidateQueries({ queryKey: ["quizzes"] })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const quizzes = (data?.data ?? []) as QuizRow[]
  const isFilterActive =
    programFilter.kind !== "all" ||
    nameFilter.trim() !== "" ||
    tagFilter.length > 0 ||
    selectedSubjects.length > 0

  const filteredQuizzes = useMemo(() => {
    const trimmed = nameFilter.trim().toLowerCase()
    return quizzes.filter((quiz) => {
      if (!trimmed) return true
      return quiz.name.toLowerCase().includes(trimmed)
    })
  }, [nameFilter, quizzes])

  const visibleQuizIds = useMemo(
    () => filteredQuizzes.map((quiz) => quiz.id),
    [filteredQuizzes],
  )
  const allVisibleSelected =
    visibleQuizIds.length > 0 &&
    visibleQuizIds.every((quizId) => selectedQuizIds.has(quizId))
  const someVisibleSelected = visibleQuizIds.some((quizId) =>
    selectedQuizIds.has(quizId),
  )
  const selectedCount = selectedQuizIds.size
  const selectedTemplate = (templatesData?.data ?? []).find(
    (template) => template.id === selectedTemplateId,
  )

  function selectQuizIds(quizIds: string[]) {
    const newQuizIds = quizIds.filter((quizId) => !selectedQuizIds.has(quizId))
    const availableSlots = MAX_BULK_QUIZZES - selectedQuizIds.size
    const quizIdsToAdd = newQuizIds.slice(0, availableSlots)

    if (quizIdsToAdd.length < newQuizIds.length) {
      showErrorToast(
        `You can select up to ${MAX_BULK_QUIZZES} quizzes at a time.`,
      )
    }

    setSelectedQuizIds((current) => {
      const next = new Set(current)
      for (const quizId of quizIdsToAdd) next.add(quizId)
      return next
    })
  }

  function toggleQuizSelection(quizId: string, checked: boolean) {
    if (checked) {
      selectQuizIds([quizId])
    } else {
      setSelectedQuizIds((current) => {
        const next = new Set(current)
        next.delete(quizId)
        return next
      })
    }
  }

  function toggleAllVisibleQuizzes() {
    if (allVisibleSelected) {
      setSelectedQuizIds((current) => {
        const next = new Set(current)
        for (const quizId of visibleQuizIds) next.delete(quizId)
        return next
      })
    } else {
      selectQuizIds(visibleQuizIds)
    }
  }

  // Dropdown value is a string for the native select
  const dropdownValue =
    programFilter.kind === "all"
      ? "all"
      : programFilter.kind === "unassigned"
        ? "unassigned"
        : programFilter.id

  function handleDropdownChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value
    if (value === "all") {
      setProgramFilter({ kind: "all" })
    } else if (value === "unassigned") {
      setProgramFilter({ kind: "unassigned" })
    } else {
      setProgramFilter({ kind: "program", id: value })
    }
  }

  const getProgramBadgeColor = (status?: string) => {
    switch (status) {
      case "active":
        return "green"
      case "inactive":
        return "orange"
      case "archived":
        return "gray"
      default:
        return "gray"
    }
  }

  function getProgramLabel(name: string, status?: string) {
    if (status === "inactive") return `${name} (Inactive)`
    if (status === "archived") return `${name} (Archived)`
    return name
  }

  const renderActionsCell = (quiz: QuizListPublic) => (
    <MenuRoot>
      <MenuTrigger asChild>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={`${quiz.name} actions`}
          data-testid="quiz-actions-trigger"
        >
          <FiMoreVertical />
        </IconButton>
      </MenuTrigger>
      <MenuContent>
        <Link to={`/quiz-management/${quiz.id}/edit`}>
          <MenuItem value="edit" gap={2}>
            <FiEdit2 />
            Edit
          </MenuItem>
        </Link>
        <Link to={`/quiz-management/${quiz.id}/analytics`}>
          <MenuItem value="analytics" gap={2}>
            <FiBarChart2 />
            View Analytics
          </MenuItem>
        </Link>
        <MenuItem
          value="duplicate"
          gap={2}
          onClick={() => duplicateQuizMutation.mutate(quiz.id)}
        >
          <FiCopy />
          Duplicate
        </MenuItem>
        <Link to={`/quiz-management/${quiz.id}/export`}>
          <MenuItem value="export" gap={2}>
            <FiDownload />
            Export
          </MenuItem>
        </Link>
        <MenuItem
          value="delete"
          gap={2}
          color="red.500"
          onClick={() => setDeleteTarget(quiz)}
        >
          <FiTrash2 />
          Delete
        </MenuItem>
      </MenuContent>
    </MenuRoot>
  )

  let content = <PageSkeleton variant="table" rows={4} />

  if (!isLoading) {
    const hasResults = filteredQuizzes.length > 0

    const tableContent =
      quizzes.length === 0 && !isFilterActive ? (
        <AppEmptyState
          title={emptyStateCopy.quizzes.managedEmpty.title}
          description={emptyStateCopy.quizzes.managedEmpty.description}
          icon={<FiSearch />}
          data-testid="quiz-management-empty-state"
        />
      ) : hasResults ? (
        <>
          {/* Desktop */}
          <Box display={{ base: "none", md: "block" }}>
            <Table.Root size={{ base: "sm", md: "md" }}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader width="44px">
                    <Checkbox
                      checked={
                        allVisibleSelected
                          ? true
                          : someVisibleSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={toggleAllVisibleQuizzes}
                      inputProps={{
                        "aria-label": "Select all visible quizzes",
                      }}
                    />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Tags</Table.ColumnHeader>
                  <Table.ColumnHeader>Programs</Table.ColumnHeader>
                  <Table.ColumnHeader>Retake</Table.ColumnHeader>
                  <Table.ColumnHeader>Result Mode</Table.ColumnHeader>
                  <Table.ColumnHeader>Questions</Table.ColumnHeader>
                  <Table.ColumnHeader>Created</Table.ColumnHeader>
                  <Table.ColumnHeader>Visibility</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">
                    Actions
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredQuizzes.map((quiz) => {
                  const questionCount = quiz.question_count ?? 0
                  const status = getQuizPublishStatus({
                    publishDate: quiz.publish_date,
                    cutoffDate: quiz.cutoff_date,
                  })
                  return (
                    <Table.Row key={quiz.id}>
                      <Table.Cell>
                        <Checkbox
                          checked={selectedQuizIds.has(quiz.id)}
                          onCheckedChange={({ checked }) =>
                            toggleQuizSelection(quiz.id, checked === true)
                          }
                          inputProps={{
                            "aria-label": `Select ${quiz.name}`,
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontWeight="medium">{quiz.name}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        {quiz.tags && quiz.tags.length > 0 ? (
                          <Flex gap={1} wrap="wrap">
                            {quiz.tags.map((tag) => (
                              <Badge key={tag} colorPalette="blue" size="sm">
                                {tag}
                              </Badge>
                            ))}
                          </Flex>
                        ) : (
                          <Text fontSize="sm" color="fg.muted">
                            No tags
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {quiz.programs && quiz.programs.length > 0 ? (
                          <Flex gap={2} wrap="wrap">
                            {quiz.programs.map((program) => (
                              <Badge
                                key={program.id}
                                colorPalette={
                                  getProgramBadgeColor(program.status) as
                                    | "green"
                                    | "orange"
                                    | "gray"
                                }
                                size="sm"
                              >
                                {program.name}
                              </Badge>
                            ))}
                          </Flex>
                        ) : (
                          <Text fontSize="sm" color="fg.muted">
                            Visible to all students
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {quiz.allow_retake ? "Allowed" : "Single attempt"}
                      </Table.Cell>
                      <Table.Cell>
                        {quiz.show_result_mode === "scheduled"
                          ? "Scheduled"
                          : "Immediate"}
                      </Table.Cell>
                      <Table.Cell>{`${questionCount} question${
                        questionCount === 1 ? "" : "s"
                      }`}</Table.Cell>
                      <Table.Cell>{formatDateTime(quiz.created_at)}</Table.Cell>
                      <Table.Cell>
                        <VStack align="flex-start" gap={1}>
                          <Badge colorPalette={status.color}>
                            {status.label}
                          </Badge>
                          <Text fontSize="xs" color="fg.muted">
                            {status.description}
                          </Text>
                        </VStack>
                      </Table.Cell>
                      <Table.Cell textAlign="center" verticalAlign="middle">
                        {renderActionsCell(quiz)}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>

          {/* Mobile cards */}
          <VStack
            display={{ base: "flex", md: "none" }}
            gap={3}
            align="stretch"
          >
            <Flex align="center" justify="space-between" px={1}>
              <Checkbox
                checked={
                  allVisibleSelected
                    ? true
                    : someVisibleSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={toggleAllVisibleQuizzes}
                inputProps={{
                  "aria-label": "Select all visible quizzes",
                }}
              >
                Select all visible quizzes
              </Checkbox>
            </Flex>
            {filteredQuizzes.map((quiz) => {
              const questionCount = quiz.question_count ?? 0
              const status = getQuizPublishStatus({
                publishDate: quiz.publish_date,
                cutoffDate: quiz.cutoff_date,
              })
              return (
                <Card.Root key={quiz.id}>
                  <Card.Body>
                    <Flex justify="space-between" align="flex-start">
                      <Flex align="flex-start" gap={3} flex="1" minW={0} mr={2}>
                        <Checkbox
                          checked={selectedQuizIds.has(quiz.id)}
                          onCheckedChange={({ checked }) =>
                            toggleQuizSelection(quiz.id, checked === true)
                          }
                          inputProps={{
                            "aria-label": `Select ${quiz.name}`,
                          }}
                        />
                        <Text fontWeight="medium" minW={0}>
                          {quiz.name}
                        </Text>
                      </Flex>
                      {renderActionsCell(quiz)}
                    </Flex>

                    {quiz.tags && quiz.tags.length > 0 ? (
                      <Flex gap={1} wrap="wrap" mt={2}>
                        {quiz.tags.map((tag) => (
                          <Badge key={tag} colorPalette="blue" size="sm">
                            {tag}
                          </Badge>
                        ))}
                      </Flex>
                    ) : (
                      <Text fontSize="xs" color="fg.muted" mt={2}>
                        No tags
                      </Text>
                    )}

                    {quiz.programs && quiz.programs.length > 0 ? (
                      <Flex gap={1} wrap="wrap" mt={1}>
                        {quiz.programs.map((program) => (
                          <Badge
                            key={program.id}
                            colorPalette={
                              getProgramBadgeColor(program.status) as
                                | "green"
                                | "orange"
                                | "gray"
                            }
                            size="sm"
                          >
                            {getProgramLabel(program.name, program.status)}
                          </Badge>
                        ))}
                      </Flex>
                    ) : (
                      <Text fontSize="xs" color="fg.muted" mt={1}>
                        Visible to all students
                      </Text>
                    )}

                    <SimpleGrid columns={2} gap={2} mt={3}>
                      <Stack gap={0}>
                        <Text fontSize="xs" color="fg.muted">
                          Retake
                        </Text>
                        <Text fontSize="sm">
                          {quiz.allow_retake ? "Allowed" : "Single attempt"}
                        </Text>
                      </Stack>
                      <Stack gap={0}>
                        <Text fontSize="xs" color="fg.muted">
                          Result Mode
                        </Text>
                        <Text fontSize="sm">
                          {quiz.show_result_mode === "scheduled"
                            ? "Scheduled"
                            : "Immediate"}
                        </Text>
                      </Stack>
                      <Stack gap={0}>
                        <Text fontSize="xs" color="fg.muted">
                          Questions
                        </Text>
                        <Text fontSize="sm">{`${questionCount} question${
                          questionCount === 1 ? "" : "s"
                        }`}</Text>
                      </Stack>
                      <Stack gap={0}>
                        <Text fontSize="xs" color="fg.muted">
                          Created
                        </Text>
                        <Text fontSize="sm">
                          {formatDateTime(quiz.created_at)}
                        </Text>
                      </Stack>
                    </SimpleGrid>

                    <Flex align="center" gap={2} mt={3}>
                      <Badge colorPalette={status.color}>{status.label}</Badge>
                      <Text fontSize="xs" color="fg.muted">
                        {status.description}
                      </Text>
                    </Flex>
                  </Card.Body>
                </Card.Root>
              )
            })}
          </VStack>
        </>
      ) : (
        <AppEmptyState
          title={emptyStateCopy.quizzes.managedFiltered.title}
          description={emptyStateCopy.quizzes.managedFiltered.description}
          icon={<FiSearch />}
          data-testid="quiz-management-filtered-empty-state"
        />
      )

    content = (
      <VStack align="stretch" gap={6}>
        <Stack gap={3}>
          <QuizFilterControls
            search={nameFilter}
            onSearchChange={setNameFilter}
            selectedSubjects={selectedSubjects}
            onSubjectsChange={setSelectedSubjects}
            availableSubjects={availableSubjects}
            selectedTags={tagFilter}
            onTagsChange={setTagFilter}
            additionalFilter={
              <Field label="Program">
                <NativeSelectRoot aria-label="program">
                  <NativeSelectField
                    value={dropdownValue}
                    onChange={handleDropdownChange}
                  >
                    <option value="all">All Programs</option>
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {getProgramLabel(program.name, program.status)}
                      </option>
                    ))}
                    <option value="unassigned">Unassigned</option>
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            }
          />
          {(nameFilter ||
            tagFilter.length > 0 ||
            selectedSubjects.length > 0) && (
            <Button
              variant="ghost"
              onClick={() => {
                setNameFilter("")
                setTagFilter([])
                setSelectedSubjects([])
              }}
            >
              Clear
            </Button>
          )}
        </Stack>
        {selectedCount > 0 && (
          <Flex
            align={{ base: "stretch", sm: "center" }}
            justify="space-between"
            direction={{ base: "column", sm: "row" }}
            gap={3}
            bg="bg.subtle"
            borderWidth="1px"
            borderColor="border"
            borderRadius="md"
            px={4}
            py={3}
          >
            <Text fontWeight="medium">
              {selectedCount} {selectedCount === 1 ? "quiz" : "quizzes"}{" "}
              selected
            </Text>
            <Flex gap={2}>
              <Button
                variant="ghost"
                onClick={() => setSelectedQuizIds(new Set())}
              >
                Clear selection
              </Button>
              <Button onClick={() => setIsBulkApplyOpen(true)}>
                Apply template
              </Button>
            </Flex>
          </Flex>
        )}
        {tableContent}
      </VStack>
    )
  }

  const breadcrumbs = useRouteBreadcrumbs("quiz-management.index")
  const actions = (
    <Link to="/quiz-management/add">
      <Button>
        <FaPlus fontSize="16px" />
        Create Quiz
      </Button>
    </Link>
  )

  return (
    <PageShell
      title="Quiz Management"
      description="Build and manage assessments for your organization."
      breadcrumbs={breadcrumbs}
      actions={actions}
      data-testid="quizzes-list"
    >
      {content}
      <DialogRoot
        open={Boolean(deleteTarget)}
        onOpenChange={({ open }) => {
          if (!open) {
            setDeleteTarget(null)
          }
        }}
      >
        <DialogContent maxW="md">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Delete quiz</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack align="stretch" gap={3}>
              <Text>
                Are you sure you want to delete{" "}
                <Text as="span" fontWeight="semibold">
                  {deleteTarget?.name}
                </Text>
                ? This action cannot be undone.
              </Text>
            </VStack>
          </DialogBody>
          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button variant="subtle" colorPalette="gray">
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              colorPalette="red"
              onClick={() => {
                if (deleteTarget) {
                  deleteQuizMutation.mutate(deleteTarget.id)
                }
              }}
              loading={deleteQuizMutation.isPending}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
      <DialogRoot
        open={isBulkApplyOpen}
        onOpenChange={({ open }) => {
          setIsBulkApplyOpen(open)
          if (!open) setSelectedTemplateId("")
        }}
      >
        <DialogContent maxW="lg">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Apply quiz template</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack align="stretch" gap={4}>
              <Field label="Template">
                <NativeSelectRoot>
                  <NativeSelectField
                    aria-label="Template"
                    value={selectedTemplateId}
                    onChange={(event) =>
                      setSelectedTemplateId(event.target.value)
                    }
                  >
                    <option value="">Choose a template</option>
                    {(templatesData?.data ?? []).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
              <Text>
                Apply{" "}
                {selectedTemplate
                  ? `"${selectedTemplate.name}"`
                  : "the selected template"}{" "}
                to {selectedCount} {selectedCount === 1 ? "quiz" : "quizzes"}?
              </Text>
              <Text>
                Timing, retake behavior, result visibility, review settings, and
                MPL settings will be replaced.
              </Text>
              <Text>
                Quiz names, schedule dates, questions, sections, program
                assignments, and tags will stay unchanged.
              </Text>
            </VStack>
          </DialogBody>
          <DialogFooter gap={2}>
            <Button
              variant="ghost"
              onClick={() => {
                setIsBulkApplyOpen(false)
                setSelectedTemplateId("")
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedTemplateId}
              loading={bulkApplyTemplateMutation.isPending}
              onClick={() => bulkApplyTemplateMutation.mutate()}
            >
              Apply to {selectedCount}{" "}
              {selectedCount === 1 ? "quiz" : "quizzes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </PageShell>
  )
}

export default QuizManagementList
